import { createFileRoute, Link } from "@tanstack/react-router";
import { useState } from "react";
import { Header } from "@/components/site/Header";
import { Footer } from "@/components/site/Footer";
import { Button } from "@/components/ui/button";
import { PeopelsLogo } from "@/components/brand/PeopelsLogo";
import { supabase } from "@/integrations/supabase/client";
import {
  Wallet, Calendar, Shield, TrendingUp, Loader2, BadgeCheck, Lock,
  Clock, RefreshCcw, CheckCircle2, UserCheck, FileCheck, CreditCard,
  Star, ChevronRight, Sparkles, MessageSquare, MapPin,
} from "lucide-react";

export const Route = createFileRoute("/become-helper")({
  head: () => ({
    meta: [
      { title: "Become a Helper — Build your reputation in Greater London | Peopels" },
      { name: "description", content: "Join Peopels early and help shape a quality-first marketplace for local services across Greater London. Flexible work, transparent fees, secure payouts." },
      { property: "og:title", content: "Become a Helper — Peopels" },
      { property: "og:description", content: "Flexible local work across Greater London. Transparent fees, secure payouts, reviews you own." },
      { property: "og:url", content: "/become-helper" },
    ],
    links: [{ rel: "canonical", href: "/become-helper" }],
  }),
  component: Page,
});

const ELIGIBILITY = [
  { label: "18 or over", note: "Required by UK law" },
  { label: "Right to work in the UK", note: "Passport, BRP, or share-code" },
  { label: "Based in Greater London", note: "We're London-only while we grow" },
  { label: "A smartphone with a camera", note: "For ID verification & job photos" },
  { label: "A UK bank account", note: "For Stripe payouts to your bank" },
];

const STEPS = [
  { icon: UserCheck, title: "Create your profile", time: "About 3 min", body: "Add a photo, your skills, hourly rate, and the parts of London you'll cover." },
  { icon: FileCheck, title: "Verify your ID", time: "About 5 min", body: "Snap your ID and a selfie. Reviewed by a real person — usually within one working day." },
  { icon: CreditCard, title: "Connect Stripe payouts", time: "About 2 min", body: "Stripe handles secure payouts to your UK bank account. No card details stay with us." },
  { icon: TrendingUp, title: "Start receiving job offers", time: "Right after approval", body: "We'll match you to nearby jobs in your categories. You choose what to accept." },
];

const BENEFITS = [
  { i: Wallet, t: "Transparent fees", d: "A clear platform fee on each job. No subscription, no joining fee, no listing fee." },
  { i: Calendar, t: "Work on your schedule", d: "Set your availability week by week. Accept only the jobs that suit you." },
  { i: Shield, t: "Secure payments", d: "Customers pay upfront into escrow. Funds release to you after the job is completed." },
  { i: Star, t: "Reviews you own", d: "Every completed job earns a public review. Your reputation travels with your profile." },
];

const WHY_EARLY = [
  { icon: Sparkles, title: "Be a founding Helper", body: "Help shape how the platform works — pricing, categories, policies. Early Helpers get direct input." },
  { icon: MessageSquare, title: "Direct line to the team", body: "You can reach a real person when something doesn't work. We respond to every message." },
  { icon: TrendingUp, title: "Build review history first", body: "Joining now means you collect reviews before the marketplace gets crowded." },
];

const FAQ = [
  { q: "What does Peopels cost?", a: "Creating a profile is free. We take a flat platform fee on each completed job — shown clearly before you accept work. No subscription, joining or listing fees." },
  { q: "When do I get paid?", a: "Customers pay upfront into escrow when they book. Funds are released to your Stripe account after the job is marked complete, then Stripe pays out to your UK bank account on its standard schedule." },
  { q: "Are you promising me work?", a: "No. We're a new marketplace growing demand in Greater London — we can't guarantee bookings. What we can promise is fair matching, honest messaging, and that we'll never invent demand that isn't there." },
  { q: "Where do you operate?", a: "Greater London only. We're focused on getting one city right before we think about expanding." },
  { q: "Which services can I offer?", a: "Right now: Handyman, Furniture Assembly, TV Mounting, and Moving Help. Pick one or several when you sign up." },
  { q: "How do I get my first job?", a: "Complete your profile, verify your ID, and turn on availability for the postcodes you cover. We match you to nearby jobs as they come in — replying quickly helps." },
  { q: "Can I leave at any time?", a: "Yes. Pause availability whenever you want, or close your account in your settings. No notice period, no exit fee." },
];

function Page() {
  const [connecting, setConnecting] = useState(false);
  const [error, setError] = useState<string | null>(null);

  async function startStripeOnboarding() {
    setError(null);
    setConnecting(true);
    try {
      const { data } = await supabase.auth.getSession();
      const token = data.session?.access_token;
      if (!token) { window.location.href = "/auth?redirect=/become-helper"; return; }
      const res = await fetch("/api/public/stripe/connect-onboard", {
        method: "POST",
        headers: { "Content-Type": "application/json", Authorization: `Bearer ${token}` },
      });
      const body = await res.json();
      if (!res.ok || !body.url) throw new Error(body.error ?? "Could not start onboarding");
      window.location.href = body.url;
    } catch (err) {
      setError(err instanceof Error ? err.message : "Stripe onboarding is unavailable right now. Please try again.");
      setConnecting(false);
    }
  }

  return (
    <div className="flex min-h-screen flex-col bg-background">
      <Header />
      <main id="main-content" className="flex-1">
        {/* Hero */}
        <section className="bg-gradient-hero text-primary-foreground">
          <div className="mx-auto max-w-7xl px-4 py-14 sm:px-6 sm:py-20 lg:px-8">
            <div className="mb-6 inline-flex rounded-2xl bg-white/95 px-4 py-3 shadow-sm">
              <PeopelsLogo variant="primary" height={56} />
            </div>
            <span className="inline-flex items-center gap-1.5 rounded-full bg-white/10 px-3 py-1 text-xs font-semibold uppercase tracking-wider backdrop-blur">
              <Sparkles className="h-3 w-3" /> Now onboarding early Helpers across Greater London
            </span>
            <h1 className="mt-4 font-display text-3xl font-extrabold leading-tight sm:text-5xl">
              Flexible local work.<br className="hidden sm:inline" /> Reputation you own.
            </h1>
            <p className="mt-4 max-w-2xl text-base text-white/85 sm:text-lg">
              Peopels is a new quality-first marketplace for local services across Greater London. Join early, set your own rates, and help shape how the platform works.
            </p>

            <div className="mt-8 flex flex-col gap-3 sm:flex-row sm:flex-wrap">
              <Button asChild size="lg" variant="secondary" className="bg-white text-secondary hover:bg-white/90">
                <Link to="/auth" search={{ mode: "signup" }}>Create my Helper account <ChevronRight className="ml-0.5 h-4 w-4" /></Link>
              </Button>
              <Button size="lg" variant="outline" className="border-white/40 bg-transparent text-white hover:bg-white/10" onClick={startStripeOnboarding} disabled={connecting} aria-busy={connecting}>
                {connecting ? <><Loader2 className="mr-2 h-4 w-4 animate-spin" />Opening secure Stripe…</> : "Connect payouts with Stripe"}
              </Button>
            </div>

            <ul className="mt-6 flex flex-wrap gap-x-6 gap-y-2 text-sm text-white/80">
              <li className="inline-flex items-center gap-1.5"><Clock className="h-4 w-4" /> Free to join</li>
              <li className="inline-flex items-center gap-1.5"><BadgeCheck className="h-4 w-4" /> No subscription, no listing fee</li>
              <li className="inline-flex items-center gap-1.5"><Lock className="h-4 w-4" /> Payouts powered by Stripe</li>
              <li className="inline-flex items-center gap-1.5"><MapPin className="h-4 w-4" /> Greater London only</li>
            </ul>

            {error && (
              <div role="alert" className="mt-6 inline-flex max-w-xl items-start gap-3 rounded-lg border border-white/20 bg-white/10 px-4 py-3 text-sm text-white backdrop-blur">
                <span className="flex-1">{error}</span>
                <button onClick={startStripeOnboarding} className="inline-flex items-center gap-1 font-semibold underline">
                  <RefreshCcw className="h-3.5 w-3.5" /> Retry
                </button>
              </div>
            )}
          </div>
        </section>

        {/* Why join */}
        <section className="mx-auto max-w-7xl px-4 py-12 sm:px-6 sm:py-16 lg:px-8">
          <h2 className="font-display text-2xl font-bold sm:text-3xl">Why join Peopels</h2>
          <p className="mt-2 max-w-2xl text-muted-foreground">A platform built around the things professionals actually ask for: clear fees, secure payments, and reviews that build a reputation worth having.</p>
          <div className="mt-8 grid gap-4 sm:grid-cols-2 sm:gap-6 lg:grid-cols-4">
            {BENEFITS.map((b) => (
              <div key={b.t} className="rounded-2xl border border-border bg-card p-5 shadow-card sm:p-6">
                <b.i className="h-5 w-5 text-primary" aria-hidden />
                <p className="mt-3 font-display font-semibold">{b.t}</p>
                <p className="mt-1 text-sm text-muted-foreground">{b.d}</p>
              </div>
            ))}
          </div>
        </section>

        {/* Why early */}
        <section className="bg-surface py-12 sm:py-16">
          <div className="mx-auto max-w-7xl px-4 sm:px-6 lg:px-8">
            <h2 className="font-display text-2xl font-bold sm:text-3xl">Why join early</h2>
            <p className="mt-2 max-w-2xl text-muted-foreground">We're honest about where we are: a new marketplace actively growing demand in London. Joining now has real advantages.</p>
            <div className="mt-8 grid gap-4 sm:grid-cols-3 sm:gap-6">
              {WHY_EARLY.map((w) => (
                <div key={w.title} className="rounded-2xl border border-border bg-card p-5 shadow-card sm:p-6">
                  <w.icon className="h-5 w-5 text-primary" aria-hidden />
                  <p className="mt-3 font-display font-semibold">{w.title}</p>
                  <p className="mt-1 text-sm text-muted-foreground">{w.body}</p>
                </div>
              ))}
            </div>
          </div>
        </section>

        {/* Eligibility + steps */}
        <section className="py-12 sm:py-16">
          <div className="mx-auto max-w-7xl px-4 sm:px-6 lg:px-8">
            <div className="grid gap-8 lg:grid-cols-2 lg:gap-12">
              <div>
                <h2 className="font-display text-2xl font-bold sm:text-3xl">What you'll need</h2>
                <p className="mt-2 text-muted-foreground">A quick checklist before you start.</p>
                <ul className="mt-6 space-y-3">
                  {ELIGIBILITY.map((e) => (
                    <li key={e.label} className="flex items-start gap-3 rounded-xl border border-border bg-card p-4">
                      <CheckCircle2 className="mt-0.5 h-5 w-5 shrink-0 text-emerald-600" />
                      <div className="min-w-0">
                        <p className="font-medium">{e.label}</p>
                        <p className="text-xs text-muted-foreground">{e.note}</p>
                      </div>
                    </li>
                  ))}
                </ul>
              </div>

              <div>
                <h2 className="font-display text-2xl font-bold sm:text-3xl">How it works</h2>
                <p className="mt-2 text-muted-foreground">Four short steps from signup to your first job offer.</p>
                <ol className="mt-6 space-y-3">
                  {STEPS.map((s, i) => (
                    <li key={s.title} className="grid grid-cols-[auto_minmax(0,1fr)] items-start gap-3 rounded-xl border border-border bg-card p-4">
                      <span className="grid h-9 w-9 shrink-0 place-items-center rounded-full bg-accent font-display text-sm font-bold text-primary">{i + 1}</span>
                      <div className="min-w-0">
                        <div className="flex flex-wrap items-baseline gap-x-2">
                          <p className="font-semibold">{s.title}</p>
                          <span className="text-xs text-muted-foreground">· {s.time}</span>
                        </div>
                        <p className="mt-1 text-sm text-muted-foreground">{s.body}</p>
                      </div>
                    </li>
                  ))}
                </ol>
              </div>
            </div>
          </div>
        </section>

        {/* FAQ */}
        <section className="bg-surface py-12 sm:py-16">
          <div className="mx-auto max-w-3xl px-4 sm:px-6 lg:px-8">
            <h2 className="font-display text-2xl font-bold sm:text-3xl">Common questions</h2>
            <div className="mt-6 space-y-2">
              {FAQ.map((f) => (
                <details key={f.q} className="group rounded-xl border border-border bg-card p-4 open:shadow-card">
                  <summary className="cursor-pointer list-none font-medium">
                    <span className="inline-flex w-full items-center justify-between gap-3">
                      {f.q}
                      <ChevronRight className="h-4 w-4 shrink-0 transition-transform group-open:rotate-90" />
                    </span>
                  </summary>
                  <p className="mt-2 text-sm text-muted-foreground">{f.a}</p>
                </details>
              ))}
            </div>
          </div>
        </section>

        {/* Final CTA */}
        <section className="mx-auto max-w-4xl px-4 py-12 text-center sm:px-6 sm:py-16 lg:px-8">
          <h2 className="font-display text-2xl font-bold sm:text-3xl">Ready to get started?</h2>
          <p className="mt-2 text-muted-foreground">Free to join. No subscription. Leave anytime.</p>
          <div className="mt-6 flex flex-col items-center gap-3 sm:flex-row sm:justify-center">
            <Button asChild size="lg" className="w-full sm:w-auto">
              <Link to="/auth" search={{ mode: "signup" }}>Create my Helper account</Link>
            </Button>
            <Button asChild size="lg" variant="outline" className="w-full sm:w-auto">
              <Link to="/helper/onboard">Continue your onboarding</Link>
            </Button>
          </div>
        </section>

        {/* Mobile sticky CTA */}
        <div className="sticky bottom-3 z-40 mx-3 mb-3 sm:hidden">
          <Button asChild size="lg" className="w-full rounded-full shadow-elegant">
            <Link to="/auth" search={{ mode: "signup" }}>Create my Helper account <ChevronRight className="ml-0.5 h-4 w-4" /></Link>
          </Button>
        </div>
      </main>
      <Footer />
    </div>
  );
}
